import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import DemoLayout from "@/components/demo/DemoLayout";
import { Search, ChevronLeft, ChevronRight, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;
const REGION_FLAGS = {
  canada: "\u{1F1E8}\u{1F1E6}", usa: "\u{1F1FA}\u{1F1F8}", jamaica: "\u{1F1EF}\u{1F1F2}",
  barbados: "\u{1F1E7}\u{1F1E7}", trinidad_tobago: "\u{1F1F9}\u{1F1F9}", panama: "\u{1F1F5}\u{1F1E6}"
};
const REGION_NAMES = {
  canada: "Canada", usa: "USA", jamaica: "Jamaica",
  barbados: "Barbados", trinidad_tobago: "Trinidad & Tobago", panama: "Panama"
};

function StatusBadge({ status }) {
  const map = { completed: "badge-success", started: "badge-warning", pending: "badge-muted" };
  const labels = { completed: "Complete", started: "In Progress", pending: "Not Started" };
  return <span className={`badge-m ${map[status] || "badge-muted"}`}>{labels[status] || status}</span>;
}

export default function DemoPeopleDirectory() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRegion = params.get("region") || "";

  const [people, setPeople] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState({});
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState(initialRegion);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [dept, setDept] = useState("");

  const loadPeople = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.set("search", search);
      if (region) p.set("region", region);
      if (status) p.set("status", status);
      if (dept) p.set("department", dept);
      p.set("page", page);
      p.set("page_size", 30);

      const res = await axios.get(`${API}/people?${p.toString()}`);
      setPeople(res.data.people);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setStatusCounts(res.data.status_counts || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, region, status, dept, page]);

  useEffect(() => { loadPeople(); }, [loadPeople]);
  useEffect(() => {
    axios.get(`${API}/departments`).then(r => setDepartments(r.data.departments)).catch(() => {});
  }, []);

  const debounceSearch = useCallback((val) => {
    setSearch(val);
    setPage(1);
  }, []);

  return (
    <DemoLayout>
      <div className="space-y-4" data-testid="people-directory">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: "var(--text-primary)" }}>People</h1>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>All {total} employees across 6 regions</p>
        </div>

        {/* Status tabs */}
        <div className="flex gap-1">
          {[
            { key: "", label: "All", count: statusCounts.all || total },
            { key: "completed", label: "Complete", count: statusCounts.completed },
            { key: "started", label: "In Progress", count: statusCounts.started },
            { key: "pending", label: "Not Started", count: statusCounts.pending },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setStatus(t.key); setPage(1); }}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium transition-all"
              style={{
                background: status === t.key ? "var(--color-accent-bg)" : "transparent",
                color: status === t.key ? "var(--color-accent)" : "var(--text-secondary)",
              }}
              data-testid={`status-tab-${t.key || "all"}`}
            >
              {t.label} <span className="ml-1 metric-val">{t.count ?? "..."}</span>
            </button>
          ))}
        </div>

        {/* Search & Filters */}
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <input
              type="text"
              placeholder="Search by name, email, role..."
              className="w-full pl-9 pr-3 py-2 rounded-md text-[13px] outline-none"
              style={{
                background: "var(--surface-card)",
                border: "var(--border-width) solid var(--border-default)",
                color: "var(--text-primary)",
              }}
              onChange={(e) => debounceSearch(e.target.value)}
              data-testid="people-search"
            />
          </div>
          <select
            value={region}
            onChange={(e) => { setRegion(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-md text-[13px]"
            style={{
              background: "var(--surface-card)",
              border: "var(--border-width) solid var(--border-default)",
              color: "var(--text-primary)",
            }}
            data-testid="region-filter"
          >
            <option value="">All Regions</option>
            {Object.entries(REGION_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select
            value={dept}
            onChange={(e) => { setDept(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-md text-[13px] max-w-[200px]"
            style={{
              background: "var(--surface-card)",
              border: "var(--border-width) solid var(--border-default)",
              color: "var(--text-primary)",
            }}
            data-testid="dept-filter"
          >
            <option value="">All Departments</option>
            {departments.map(d => <option key={d.name} value={d.name}>{d.name} ({d.count})</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="card-m overflow-hidden">
          <table className="w-full text-[13px]" data-testid="people-table">
            <thead>
              <tr style={{ background: "var(--surface-muted)" }}>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>NAME</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>MANAGER</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>REGION</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>TYPE</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>PROGRESS</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-12 text-center" style={{ color: "var(--text-muted)" }}>Loading...</td></tr>
              ) : people.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center" style={{ color: "var(--text-muted)" }}>No results found</td></tr>
              ) : people.map((p) => {
                const initials = p.full_name ? p.full_name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "??";
                return (
                  <tr key={p.id} className="transition-colors cursor-pointer" style={{ borderBottom: "var(--border-width) solid var(--border-subtle)" }}
                    onClick={() => navigate(`/executive/demo/people/${p.id}`)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                          style={{ background: "var(--color-accent-bg)", color: "var(--color-accent)" }}>{initials}</div>
                        <div>
                          <div className="font-medium" style={{ color: "var(--text-primary)" }}>{p.full_name}</div>
                          <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{p.job_title || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5" style={{ color: "var(--text-secondary)" }}>{p.manager_name || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span>{REGION_FLAGS[p.region] || ""} <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{REGION_NAMES[p.region] || p.region}</span></span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="badge-m badge-info text-[10px]">{p.assessment_type === "manager_validation" ? "MV" : "SA"}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="progress-m flex-1 max-w-[80px]">
                          <div className="progress-fill" style={{
                            width: `${p.progress}%`,
                            background: p.status === "completed" ? "var(--color-success)" : p.status === "started" ? "var(--color-accent)" : "var(--text-muted)",
                          }} />
                        </div>
                        <StatusBadge status={p.status} />
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Eye className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-[12px]" style={{ color: "var(--text-muted)" }}>
              Page {page} of {pages} ({total} total)
            </div>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
                className="px-3 py-1.5 rounded-md text-[12px] card-m disabled:opacity-50">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages}
                className="px-3 py-1.5 rounded-md text-[12px] card-m disabled:opacity-50">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </DemoLayout>
  );
}
